import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Download, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { 
  exportToCSV, 
  exportToPDF, 
  prepareKPIsForCSV, 
  prepareRevenueDataForCSV, 
  prepareInsightsForCSV,
  prepareMarketingDataForCSV
} from '@/utils/exportUtils';
import { KPICard, ChartData, InsightData } from '@/data/mockData';
import { useToast } from '@/hooks/use-toast';

interface ExportDialogProps {
  kpis: KPICard[];
  revenueData: ChartData[];
  engagementData: ChartData[];
  marketingData: ChartData[];
  insights: InsightData[];
  children: React.ReactNode;
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  kpis,
  revenueData,
  engagementData,
  marketingData,
  insights,
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [selectedSections, setSelectedSections] = useState({
    kpis: true,
    revenue: true,
    engagement: true,
    marketing: true,
    insights: true
  });
  const { toast } = useToast();

  const handleSectionChange = (section: keyof typeof selectedSections, checked: boolean) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: checked
    }));
  };

  const generateFilename = () => {
    const date = new Date().toISOString().split('T')[0];
    const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
    return `dashboard-analytics-${date}-${time}`;
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const filename = generateFilename();
      
      if (exportFormat === 'csv') {
        // Export multiple CSV files for different sections
        if (selectedSections.kpis) {
          const kpiData = prepareKPIsForCSV(kpis);
          exportToCSV(kpiData, `${filename}-kpis`);
        }
        
        if (selectedSections.revenue) {
          const revenueCSVData = prepareRevenueDataForCSV(revenueData);
          exportToCSV(revenueCSVData, `${filename}-receita`);
        }
        
        if (selectedSections.engagement) {
          const engagementCSVData = engagementData.map(item => ({
            'Dia': item.name,
            'Taxa de Engajamento (%)': item.value,
            'Data': item.date || ''
          }));
          exportToCSV(engagementCSVData, `${filename}-engajamento`);
        }
        
        if (selectedSections.marketing) {
          const marketingCSVData = prepareMarketingDataForCSV(marketingData);
          exportToCSV(marketingCSVData, `${filename}-marketing`);
        }
        
        if (selectedSections.insights) {
          const insightsCSVData = prepareInsightsForCSV(insights);
          exportToCSV(insightsCSVData, `${filename}-insights`);
        }
        
        toast({
          title: "Exportação CSV concluída",
          description: "Arquivos CSV baixados com sucesso",
        });
      } else {
        // Export single PDF with all selected sections
        await exportToPDF(kpis, revenueData, insights, filename);
        
        toast({
          title: "Exportação PDF concluída",
          description: "Relatório PDF baixado com sucesso",
        });
      }
      
      setIsOpen(false);
    } catch (error) {
      console.error('Erro na exportação:', error);
      toast({
        title: "Erro na exportação",
        description: "Ocorreu um erro ao exportar os dados. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const selectedCount = Object.values(selectedSections).filter(Boolean).length;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Download className="h-5 w-5" />
            <span>Exportar Dados</span>
          </DialogTitle>
          <DialogDescription>
            Escolha o formato e as seções que deseja exportar
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Formato de Exportação</Label>
            <Select value={exportFormat} onValueChange={(value: 'csv' | 'pdf') => setExportFormat(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">
                  <div className="flex items-center space-x-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    <span>CSV (Excel)</span>
                  </div>
                </SelectItem>
                <SelectItem value="pdf">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span>PDF (Relatório)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            {exportFormat === 'csv' && (
              <p className="text-xs text-muted-foreground">
                Cada seção será exportada como um arquivo CSV separado
              </p>
            )}
            {exportFormat === 'pdf' && (
              <p className="text-xs text-muted-foreground">
                Todas as seções selecionadas serão incluídas em um único relatório PDF
              </p>
            )}
          </div>
          
          {/* Section Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Seções para Exportar ({selectedCount} selecionadas)
            </Label>
            
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="kpis"
                    checked={selectedSections.kpis}
                    onCheckedChange={(checked) => handleSectionChange('kpis', checked as boolean)}
                  />
                  <Label htmlFor="kpis" className="text-sm">
                    KPIs e Métricas Principais
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="revenue"
                    checked={selectedSections.revenue}
                    onCheckedChange={(checked) => handleSectionChange('revenue', checked as boolean)}
                  />
                  <Label htmlFor="revenue" className="text-sm">
                    Dados de Receita
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="engagement"
                    checked={selectedSections.engagement}
                    onCheckedChange={(checked) => handleSectionChange('engagement', checked as boolean)}
                  />
                  <Label htmlFor="engagement" className="text-sm">
                    Dados de Engajamento
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="marketing"
                    checked={selectedSections.marketing}
                    onCheckedChange={(checked) => handleSectionChange('marketing', checked as boolean)}
                  />
                  <Label htmlFor="marketing" className="text-sm">
                    Dados de Marketing
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="insights"
                    checked={selectedSections.insights}
                    onCheckedChange={(checked) => handleSectionChange('insights', checked as boolean)}
                  />
                  <Label htmlFor="insights" className="text-sm">
                    Insights e Recomendações
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Export Button */}
          <div className="flex space-x-2">
            <Button
              onClick={handleExport}
              disabled={isExporting || selectedCount === 0}
              className="flex-1"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Exportar {exportFormat.toUpperCase()}
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isExporting}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};